import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  buildComputeSkuSuggestionFilter,
  buildPricingUrl,
  getComputeSkuSuggestions,
} from '../config/pricingConfig';

const fetchPricingData = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error – status ${response.status}`);
  }

  return response.json();
};

/**
 * Custom hook for fetching Azure Retail Prices.
 *
 * @param {object} params
 * @param {string} params.queryKey   - Unique cache key (e.g. 'compute')
 * @param {string} params.filter     - OData $filter string for the Azure Prices API
 * @param {boolean} params.enabled   - Whether to run the query
 * @returns React Query result object with additional `items` / `nextPageLink`
 */
export function usePricingFetch({ queryKey, filter, enabled }) {
  const url = enabled ? buildPricingUrl(`$filter=${filter}`) : null;

  const result = useQuery({
    queryKey: ['pricing', queryKey, filter],
    queryFn: async () => {
      const data = await fetchPricingData(url);
      if (!data.Items || data.Items.length === 0) {
        throw new Error('No pricing data found for the specified criteria.');
      }
      return data;
    },
    enabled: !!enabled && !!filter,
    staleTime: 5 * 60 * 1000, // cache results for 5 minutes
    retry: 1,
  });

  return {
    ...result,
    items: result.data?.Items ?? [],
    nextPageLink: result.data?.NextPageLink ?? null,
  };
}

export function useComputeSkuSuggestions(searchTerm) {
  const trimmedSearchTerm = searchTerm.trim();
  const filter = trimmedSearchTerm.length >= 2
    ? buildComputeSkuSuggestionFilter(trimmedSearchTerm)
    : '';
  const url = filter ? buildPricingUrl(`$filter=${filter}&$top=25`) : null;

  const result = useQuery({
    queryKey: ['pricing', 'compute-sku-suggestions', trimmedSearchTerm],
    queryFn: () => fetchPricingData(url),
    enabled: !!url,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const suggestions = useMemo(
    () => getComputeSkuSuggestions(result.data?.Items ?? [], trimmedSearchTerm),
    [result.data?.Items, trimmedSearchTerm]
  );

  return {
    ...result,
    suggestions,
  };
}

/**
 * Fetch the next page of results given a NextPageLink URL.
 * Returns the raw API response object.
 */
export async function fetchNextPage(nextPageLink) {
  const url = buildPricingUrl(null, nextPageLink);
  return fetchPricingData(url);
}
